
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useAuthContext } from '../context/AuthContext';
import { API } from '../utils/api';
import { supabase } from '../lib/supabase';
import { sanitizePhone, sanitizeSwish, sanitizeOrgNumber, normalizeSwedishPhone, normalizeSwish, swedishMobileError } from '../utils/inputSanitizers';
import { LanguageSelector } from '../components/LanguageSelector';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export function AuthPage() {
  const { save, setProfile, clear } = useAuthContext();
  const { t } = useTranslation();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const location = useLocation();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  
  // Check if we have Google OAuth user data to pre-fill
  const googleUser = location.state?.googleUser;
  const isGoogleComplete = location.state?.isGoogleComplete;

  const [mode, setMode] = useState(isGoogleComplete ? 'register' : 'login');
  const [role, setRole] = useState('personal');
  const [phoneError, setPhoneError] = useState('');
  // Blocking auth errors (e.g. suspended account) — also handed over from the OAuth callback.
  const [authError, setAuthError] = useState(() => {
    const m = sessionStorage.getItem('auth_error') || '';
    sessionStorage.removeItem('auth_error');
    return m;
  });
  const [form, setForm] = useState({ 
    full_name: googleUser?.full_name || '', 
    phone: '', 
    email: googleUser?.email || '', 
    password: isGoogleComplete ? 'google-oauth-no-password' : '', 
    nickname: googleUser?.nickname || '', 
    profile_icon: googleUser?.profile_icon || '', 
    swish_payee_alias: '', 
    accepted_terms: false, 
    birthdate: '', 
    company_name: '', 
    org_number: '' 
  });
  const [rememberMe, setRememberMe] = useState(true);
  const [verificationStep, setVerificationStep] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  
  // Auto-fill saved credentials on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('remembered_email');
    const savedPassword = localStorage.getItem('remembered_password');
    if (savedEmail) {
      setForm(f => ({ ...f, email: savedEmail }));
      setRememberMe(true);
    }
    if (savedPassword) {
      setForm(f => ({ ...f, password: savedPassword }));
      setRememberMe(true);
    }
  }, []);

  // Which social login providers to render is decided server-side (Supabase project
  // settings). Never hardcode provider buttons — if Apple is off in Supabase, hide it.
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/auth/providers`)
      .then((res) => { if (mounted) setProviders(res.data?.providers || []); })
      .catch(() => { if (mounted) setProviders([]); });
    return () => { mounted = false; };
  }, []);

  // Org invitation signup links (Slice B): /?mode=register&invite=<id>&email=...&phone=...
  // Opens the register form prefilled so the invitation auto-attaches after signup.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('mode') === 'register') {
      setMode('register');
      const inviteEmail = params.get('email');
      const invitePhone = params.get('phone');
      setForm(f => ({
        ...f,
        email: inviteEmail || f.email,
        phone: invitePhone || f.phone,
        password: '',
      }));
      if (params.get('invite')) {
        toast.info(t('auth.inviteSignupHint', 'Create your account to accept the organisation invitation'));
      }
    }
  }, [location.search]);

  // Personal accounts: Swish is always derived from the mobile number (not separately editable).
  useEffect(() => {
    if (role === 'personal') {
      setForm(prev => ({ ...prev, swish_payee_alias: sanitizeSwish(form.phone) }));
    }
  }, [role, form.phone]);

  const onChange = (e) => {
    const { name, value } = e.target;
    // Swish: only digits, always '+46' prefixed.
    if (name === 'swish_payee_alias') {
      setForm(f => ({ ...f, swish_payee_alias: sanitizeSwish(value) }));
      return;
    }
    // Phone: only digits and a leading '+'. Personal accounts mirror phone → Swish.
    if (name === 'phone') {
      const clean = sanitizePhone(value);
      setPhoneError('');
      if (role === 'personal') {
        setForm(f => ({ ...f, phone: clean, swish_payee_alias: sanitizeSwish(clean) }));
      } else {
        setForm(f => ({ ...f, phone: clean }));
      }
      return;
    }
    if (name === 'org_number') {
      setForm(f => ({ ...f, org_number: sanitizeOrgNumber(value) }));
      return;
    }
    setForm(f => ({ ...f, [name]: value }));
  };

  // Social login via Supabase Auth (OIDC + PKCE). Supabase redirects back to /auth/callback
  // with a code that AuthCallback exchanges for a session. Replaces the old Emergent flow.
  const handleSocialLogin = async (provider) => {
    if (!supabase) {
      toast.error('Social login is not configured. Please use email and password.');
      return;
    }
    const redirectTo = window.location.origin + '/auth/callback';
    const { error } = await supabase.auth.signInWithOAuth({
      provider, // 'google' | 'apple'
      options: { redirectTo },
    });
    if (error) {
      toast.error(error.message || 'Sign in failed');
    }
  };

  const handleGoogleLogin = () => handleSocialLogin('google');
  const handleAppleLogin = () => handleSocialLogin('apple');

  // Abandon a half-finished social signup: drop the Supabase session and local
  // auth state, then return to a clean sign-in screen. Prevents being "stuck" in
  // an active session that never got past the OTP/profile step.
  const handleStartOver = async () => {
    try { if (supabase) await supabase.auth.signOut(); } catch (_) { /* noop */ }
    clear();
    setVerificationStep(false);
    setOtpCode('');
    setMode('login');
    setForm((f) => ({ ...f, password: '' }));
    navigate('/', { replace: true, state: {} });
  };
  
  const getRecaptchaToken = async (action) => {
    const siteKey = window._env_?.REACT_APP_RECAPTCHA_SITE_KEY || process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (!siteKey || siteKey.includes('PLACEHOLDER')) {
      return null;
    }
    if (!executeRecaptcha) {
      return null;
    }
    return await executeRecaptcha(action);
  };

  const isValidEmail = (email) => /.+@.+\..+/.test(email);
  const minorIcons = ['🐻', '🐼', '🐱', '🦊', '🐸', '🐵', '🐧', '🦄'];

  // V5.1: OTP-first registration flow
  const doRegister = async () => { 
    if (!form.accepted_terms) return toast.error(t('gdpr.acceptRequired'));
    if (!isValidEmail(form.email)) return toast.error('Invalid email format');
    if (role === 'business') {
      if (!form.company_name) return toast.error(t('auth.companyNameRequired'));
      if (!form.org_number || !form.org_number.trim()) return toast.error(t('auth.orgNumberRequired'));
    }
    const phoneErr = swedishMobileError(form.phone);
    if (phoneErr) { setPhoneError(phoneErr); return; }

    // Canonicalise to E.164 so OTP, duplicate checks and storage all see '+46...'.
    const phone = normalizeSwedishPhone(form.phone);
    const swish = role === 'personal' ? phone : normalizeSwish(form.swish_payee_alias);
    setForm(f => ({ ...f, phone, swish_payee_alias: swish }));

    try {
      await axios.post(`${API}/verify/phone/send-otp`, { phone });
      toast.success('Verification code sent to your phone');
      setVerificationStep(true);
      setResendCooldown(30);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 422 && typeof detail === 'string') { setPhoneError(detail); setVerificationStep(false); return; }
      toast.error(detail || 'Failed to send verification code');
    }
  };

  const verifyAndRegister = async () => {
    if (!otpCode || otpCode.length < 6) return toast.error('Enter the 6-digit code');
    try {
      setVerifyLoading(true);

      // --- Social (Supabase OAuth) signup completion --------------------------
      // Upgrade the provisional account IN PLACE via a single endpoint. The
      // backend verifies the OTP itself, so we do NOT pre-consume it here, and we
      // never call /auth/register (that is what produced "Email already registered").
      if (isGoogleComplete) {
        if (!supabase) { toast.error('Social login is not configured.'); return; }
        const { data: sess } = await supabase.auth.getSession();
        const sbToken = sess?.session?.access_token;
        if (!sbToken) { toast.error('Session expired. Please sign in again.'); return; }
        const acctType = role === 'business' ? 'business' : 'personal';
        const res = await axios.post(`${API}/auth/social/complete-registration`, {
          account_type: acctType,
          phone_e164: form.phone,
          otp_code: otpCode,
          swish_payee_alias: acctType === 'personal' ? form.swish_payee_alias : null,
          profile: {
            full_name: form.full_name || googleUser?.full_name,
            nickname: form.nickname,
            company_name: form.company_name,
            org_number: form.org_number,
          },
        }, { headers: { Authorization: `Bearer ${sbToken}` } });
        // Social users authenticate via the live Supabase JWT (picked up by
        // AuthContext); just refresh the profile in context — no legacy token.
        setProfile(res.data);
        toast.success(acctType === 'business' ? t('auth.businessRegistered') : 'Profile completed!');
        setVerificationStep(false);
        setOtpCode('');
        return;
      }

      // --- Normal (email/password) registration -------------------------------
      const verifyRes = await axios.post(`${API}/verify/phone/verify-otp`, {
        phone: form.phone,
        code: otpCode,
      });
      const verificationToken = verifyRes.data.verification_token;

      const recaptcha_token = await getRecaptchaToken('signup');
      await axios.post(`${API}/auth/register`, {
        ...form,
        role,
        recaptcha_token,
        phone_verification_token: verificationToken,
      });

      if (role === 'business') {
        toast.success(t('auth.businessRegistered'));
      } else {
        toast.success('Account created successfully!');
      }
      try { await axios.post(`${API}/verify/email/start`, { email: form.email }); } catch (_) {}
      setVerificationStep(false);
      setOtpCode('');
      setMode('login');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifyLoading(false);
    }
  };

  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const resendCode = async () => {
    if (resendCooldown > 0) return;
    try {
      await axios.post(`${API}/verify/phone/send-otp`, { phone: form.phone });
      toast.success('New code sent');
      setResendCooldown(30);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend');
    }
  };

  const goBackToForm = () => {
    setVerificationStep(false);
    setOtpCode('');
  };

    const doLogin = async () => { 
    try { 
      if (!isValidEmail(form.email)) {
        return toast.error('Invalid email format');
      }
      const recaptcha_token = await getRecaptchaToken('login');
      const res = await axios.post(`${API}/auth/login`, { email: form.email, password: form.password, recaptcha_token }); 
      
      // Save email if "Remember me" is checked
      if (rememberMe) {
        localStorage.setItem('remembered_email', form.email);
        localStorage.setItem('remembered_password', form.password);
      } else {
        localStorage.removeItem('remembered_email');
        localStorage.removeItem('remembered_password');
      }
      
      save(res.data.token, res.data.user); 
      toast.success(t('auth.loginSuccess'));
    } catch (e) { 
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && typeof detail === 'string') setAuthError(detail);
      toast.error(detail || t('auth.invalidCredentials')); 
    } 
  };
  
  return (
    <div className="min-h-screen bg-green-50">
      <div className="max-w-5xl mx-auto p-6">
        {/* Header with Language Selector */}
        <div className="flex justify-between items-start py-10">
          <div>
            <h1 className="text-5xl font-semibold tracking-tight" data-testid="app-title">PayMig V7.22</h1>
            <p className="text-muted-foreground mt-2">{t('auth.signInToContinue')}</p>
          </div>
          <LanguageSelector />
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          <Card>
            <CardHeader>
              <CardTitle>{mode === 'login' ? t('auth.login') : t('auth.register')}</CardTitle>
              <CardDescription>
                {mode === 'login' ? t('auth.welcomeBack') : t('auth.createAccount')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Google OAuth user completion banner */}
              {isGoogleComplete && mode === 'register' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2 space-y-2">
                  <p className="text-sm text-blue-800">
                    <strong>{googleUser?.full_name}</strong> — signed in socially. Please complete your registration below.
                  </p>
                  <button
                    type="button"
                    className="text-xs text-blue-700 underline"
                    onClick={handleStartOver}
                    data-testid="social-start-over-btn"
                  >
                    Not you? Start over
                  </button>
                </div>
              )}

              {/* Social login buttons — rendered ONLY for providers enabled in Supabase
                  (sourced solely from GET /api/auth/providers; no hardcoded fallback).
                  Hidden entirely while completing a social signup. When the endpoint
                  returns [] (all disabled or Supabase unreachable) we show a friendly
                  message instead of any social buttons. */}
              {!isGoogleComplete && mode === 'login' && providers.length === 0 && (
                <div
                  data-testid="no-social-providers-msg"
                  className="rounded-md border border-dashed border-muted-foreground/30 bg-muted/30 px-3 py-2 text-center text-xs text-muted-foreground"
                >
                  No sign-in options available. Please try again later.
                </div>
              )}

              {!isGoogleComplete && providers.length > 0 && (
                <>
                  {providers.includes('google') && (
                    <Button
                      variant="outline"
                      className="w-full gap-2"
                      onClick={handleGoogleLogin}
                      data-testid="google-login-btn"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Continue with Google
                    </Button>
                  )}

                  {providers.includes('apple') && (
                    <Button
                      variant="outline"
                      className="w-full gap-2"
                      onClick={handleAppleLogin}
                      data-testid="apple-login-btn"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M16.365 1.43c0 1.14-.42 2.2-1.12 3-.78.88-2.05 1.56-3.1 1.48-.13-1.1.42-2.27 1.07-3 .73-.83 2.04-1.46 3.15-1.48zM20.5 17.2c-.57 1.32-.85 1.9-1.58 3.06-1.02 1.62-2.46 3.64-4.25 3.66-1.59.01-2-.97-4.16-.96-2.15.01-2.6.99-4.2.97-1.78-.02-3.14-1.85-4.16-3.47C-.45 19.1-.96 14.6.94 12.04c1.05-1.42 2.7-2.32 4.27-2.32 1.6 0 2.61.97 3.93.97 1.28 0 2.06-.98 3.91-.98 1.4 0 2.88.76 3.93 2.07-3.45 1.89-2.89 6.81.61 8.42z" />
                      </svg>
                      Continue with Apple
                    </Button>
                  )}

                  <div className="relative my-4">
                    <Separator />
                    <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-xs text-muted-foreground">
                      or
                    </span>
                  </div>
                </>
              )}

              {mode !== 'login' && (
                <>
                  <div>
                    <Label htmlFor="full_name">{t('auth.fullName')} *</Label>
                    <Input data-testid="register-full-name" name="full_name" id="full_name" value={form.full_name} onChange={onChange} />
                  </div>
                  <div>
                    <Label htmlFor="phone">{t('auth.phone')} *</Label>
                    <Input data-testid="phone-input-register" name="phone" id="phone" placeholder="070-123 45 67" value={form.phone} onChange={onChange} inputMode="tel"
                      aria-invalid={!!phoneError} className={phoneError ? 'border-red-500' : ''} />
                    {phoneError && <p className="text-xs text-red-600 mt-1" data-testid="phone-error-register">{phoneError}</p>}
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Label htmlFor="nickname">{t('auth.nickname')} *</Label>
                      <Input data-testid="register-nickname" name="nickname" id="nickname" value={form.nickname} onChange={onChange} />
                    </div>
                    <div className="flex-1">
                      <Label htmlFor="profile_icon">{t('auth.profileIconUrl')}</Label>
                      <Input data-testid="register-profile-icon" name="profile_icon" id="profile_icon" value={form.profile_icon} onChange={onChange} />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="role">{t('auth.accountType')}</Label>
                    <div className="flex gap-2">
                      <Button type="button" variant={role==='personal'?'default':'secondary'} onClick={()=>setRole('personal')} data-testid="role-personal">Personal</Button>
                      <Button type="button" variant={role==='business'?'default':'secondary'} onClick={()=>setRole('business')} data-testid="role-business">Business</Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Personal: Individual Swish account for sending & receiving<br />
                      Business: Company account with sub-accounts & team management
                    </p>
                  </div>
                  {role === 'personal' && (
                    <div>
                      <Label htmlFor="swish_payee_alias">{t('profile.swishNumber')} *</Label>
                      <Input data-testid="swish-input-register" name="swish_payee_alias" id="swish_payee_alias" placeholder="Same as your mobile number" value={form.swish_payee_alias} readOnly disabled inputMode="text" />
                      <p className="text-xs text-muted-foreground mt-1">Your Swish number is your mobile number</p>
                    </div>
                  )}
                  {role === 'business' && (
                    <>
                      <div>
                        <Label htmlFor="company_name">{t('auth.companyName')} *</Label>
                        <Input data-testid="register-company-name" name="company_name" id="company_name" value={form.company_name} onChange={onChange} required />
                      </div>
                      <div>
                        <Label htmlFor="org_number">{t('auth.orgNumber')} *</Label>
                        <Input data-testid="orgnumber-input-register" name="org_number" id="org_number" value={form.org_number} onChange={onChange} inputMode="numeric" placeholder="e.g., 5566778899" required />
                      </div>
                    </>
                  )}
                  <div className="flex items-center space-x-2">
                    <Checkbox id="terms" checked={form.accepted_terms} onCheckedChange={(c) => setForm({...form, accepted_terms: c})} />
                    <label htmlFor="terms" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      {t('gdpr.acceptTerms')} <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">{t('settings.termsOfService')}</a>
                    </label>
                  </div>
                  {verificationStep && (
                    <Dialog open={verificationStep} onOpenChange={(open) => { if (!open) goBackToForm(); }}>
                      <DialogContent className="max-w-sm">
                        <DialogHeader>
                          <DialogTitle>Verify Your Phone</DialogTitle>
                          <DialogDescription>
                            Enter the 6-digit code sent to <strong>{form.phone}</strong>
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-2">
                          <Input
                            data-testid="phone-otp-input"
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="000000"
                            maxLength={6}
                            className="text-center text-2xl tracking-widest font-mono"
                            autoFocus
                          />
                        </div>
                        <DialogFooter className="flex-col gap-2 sm:flex-col">
                          <Button
                            onClick={verifyAndRegister}
                            disabled={verifyLoading || otpCode.length < 6}
                            className="w-full"
                            data-testid="phone-verify-button"
                          >
                            {verifyLoading ? 'Verifying & Creating Account...' : 'Verify & Create Account'}
                          </Button>
                          <div className="flex gap-2 w-full">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={resendCode}
                              disabled={resendCooldown > 0}
                              className="flex-1"
                              data-testid="phone-resend-button"
                            >
                              {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend Code'}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={goBackToForm}
                              className="flex-1"
                              data-testid="back-to-form-button"
                            >
                              Modify Details
                            </Button>
                          </div>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </>
              )}
              <div>
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input data-testid="auth-email" name="email" id="email" type="email" value={form.email} onChange={onChange} disabled={isGoogleComplete} />
              </div>
              {!isGoogleComplete && (
                <div>
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <Input data-testid="auth-password" name="password" id="password" type="password" value={form.password} onChange={onChange} />
                </div>
              )}
              {mode === 'login' ? (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="remember-me" 
                        checked={rememberMe} 
                        onCheckedChange={(c) => setRememberMe(c)} 
                      />
                      <label htmlFor="remember-me" className="text-sm font-medium leading-none cursor-pointer">
                        Remember me
                      </label>
                    </div>
                    <a href="/forgot-password" className="text-xs text-blue-600 hover:underline">{t('auth.forgotPassword')}</a>
                  </div>
                  {authError && (
                    <div role="alert" className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="auth-blocking-error">{authError}</div>
                  )}
                  <Button data-testid="login-submit" className="w-full" onClick={doLogin}>{t('auth.login')}</Button>
                </>
              ) : (
                <Button data-testid="register-submit" onClick={doRegister}>{t('auth.createAccount')}</Button>
              )}
              <div className="text-sm">
                {mode === 'login' ? (
                  <button className="underline" data-testid="switch-to-register" onClick={()=>setMode('register')}>{t('auth.noAccount')} {t('auth.register')}</button>
                ) : (
                  <button className="underline" data-testid="switch-to-login" onClick={()=>setMode('login')}>{t('auth.hasAccount')} {t('auth.login')}</button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>How it works</CardTitle>
              <CardDescription>1) Register as Personal or Business. 2) Connect your Swish account. 3) Send/receive payments. 4) Manage sub-accounts & teams. 5) Monitor transactions in real-time!</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
      <Toaster richColors />
    </div>
  );
}
